import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { WIPCampaignMetrics } from "../../../store/apis/wipApi";
import {
  LineItemBudgetChange,
  LineItemBudgetTarget,
  useAcceptAssociatedCampaignIdealMarginMutation,
} from "../../../store/apis/associatedCampaignMarginApi";
import { setToastMessage } from "../../../store/slices/app/appSlice";
import {
  fmtMoney,
  fmtMoneyDetailed,
  spreadsheetCell,
} from "../../../utils/format";
import {
  TableViewConfig,
  useTableViewConfig,
} from "../../../hooks/useTableViewConfig";
import {
  ALL_COLUMNS,
  ColumnDef,
  columnsFor,
  defaultColumnWidth,
  DIRECT_BUY_ACCENT,
  DIRECT_BUY_BG,
  EDITABLE_BORDER,
  EMPHASIS_BG,
  SECTOR_LABEL,
  SectorId,
  STATUS_COLOR,
  TONE_STYLE,
  IDEAL_BG,
} from "./columns";
import {
  applyMarginDraft,
  EnrichedRow,
  enrichRow,
  investmentForMargin,
  isMarginEditable,
  getErrorMessage,
  marginFromText,
  parseMarginDraft,
} from "./derive";
import MarginEditCell from "./MarginEditCell";
import { DirectBuyMetricCell } from "./ManualMetricEditCell";
import AcceptIdealDialog, { AcceptIdealRequest } from "./AcceptIdealDialog";
import WipLegend from "./WipLegend";
import LineItemsPanel from "./LineItemsPanel";
import Filters, { FacetOption } from "./Filters";
import { FILTER_FIELDS } from "./filterFields";
import ColumnVisibilityMenu from "./ColumnVisibilityMenu";
import ColumnInfoDialog from "./ColumnInfoDialog";
import ViewsMenu from "./ViewsMenu";
import { orderColumns, sortRows } from "./tableOrder";
import { ColumnTooltip, getColumnMeta, SourceBadge } from "./columnMeta";
import {
  computeTotals,
  formatTotal,
  getColumnTotal,
  totalCsvValue,
} from "./columnTotals";
import { BUY_MEDIA_TABLE, WipTableVariant } from "./tableVariants";

const headSx = {
  fontWeight: 700,
  whiteSpace: "nowrap" as const,
  fontSize: 12,
  px: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const cellSx = {
  whiteSpace: "nowrap" as const,
  fontSize: 12,
  px: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// Sector-row height — the column-label row sticks just below it.
const SECTOR_ROW_HEIGHT = 33;

/** Blend a hex color over an opaque surface at the given alpha → an opaque
 * rgb string. Used for frozen (sticky) cells, which must be opaque to cover
 * scrolled content while still matching the row's translucent status tint.
 * The surface must be the real paper color, not a hardcoded white: blending
 * over white in dark mode turned the pinned columns into near-white cells
 * with light text on top, i.e. unreadable. */
const flattenOnSurface = (hex: string, a: number, surface: string): string => {
  const parse = (c: string): [number, number, number] => {
    const h = c.replace("#", "");
    const full =
      h.length === 3
        ? h
            .split("")
            .map((d) => d + d)
            .join("")
        : h;
    const n = parseInt(full, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  const [sr, sg, sb] = parse(surface);
  const [r, g, b] = parse(hex);
  const mix = (sc: number, c: number) => Math.round(sc * (1 - a) + c * a);
  return `rgb(${mix(sr, r)}, ${mix(sg, g)}, ${mix(sb, b)})`;
};

const ROW_TINT_ALPHA = 0.16;

interface BuyMediaWipTableProps {
  title: string;
  rows: WIPCampaignMetrics[];
  /** Role may change a line's margin (ADMIN, STRATEGY, TECHNICIAN, PRICING). */
  canEditMargin?: boolean;
  /** Role may type in the real metrics of a direct buy (same roles as the margin). */
  canEditDirectBuys?: boolean;
  /** Admins also see the admin-only columns (Proveedor real). */
  isAdmin?: boolean;
  /** Which WIP table this is: its stored view, default columns and views. */
  variant?: WipTableVariant;
}

const BuyMediaWipTable: React.FC<BuyMediaWipTableProps> = ({
  title,
  rows,
  canEditMargin = false,
  canEditDirectBuys = false,
  isAdmin = false,
  variant = BUY_MEDIA_TABLE,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Conditional-formatting inks/backgrounds for the active palette mode —
  // the light-mode set is unreadable on a dark surface.
  const toneStyle = TONE_STYLE[theme.palette.mode];

  // ── State (persisted in localStorage) ────────────────────────────────────
  const {
    config,
    setVisibleColumnIds,
    setStatusFilter,
    setTextFilter,
    setFieldFilter,
    setColumnWidth,
    setColumnOrder,
    setSort,
    applyConfig,
  } = useTableViewConfig(variant.storageKey, variant.defaultConfig);
  // Columns this user may see: the admin-only ones are left out for the rest.
  const columns = useMemo(() => columnsFor(isAdmin), [isAdmin]);
  const {
    visibleColumnIds,
    statusFilter,
    textFilter,
    fieldFilters,
    columnWidths,
    columnOrder,
    sort,
  } = config;

  // Which view is loaded ("preset:<id>" or a saved view id), per browser.
  const [activeViewId, setActiveViewId] = useState<string>(() => {
    try {
      return localStorage.getItem(variant.activeViewKey) ?? "preset:standard";
    } catch {
      return "preset:standard";
    }
  });
  const applyView = (next: TableViewConfig, viewId: string) => {
    applyConfig(next);
    setActiveViewId(viewId);
    try {
      localStorage.setItem(variant.activeViewKey, viewId);
    } catch {
      // Blocked storage: the view still applies for this session.
    }
  };

  // Column being dragged to a new position, and the column it is over.
  const [dragColId, setDragColId] = useState<string | null>(null);
  const [dropColId, setDropColId] = useState<string | null>(null);
  // Show default-visible columns that did not exist when the view was saved.
  useEffect(() => {
    const allIds = ALL_COLUMNS.map((c) => c.id);
    let known: string[];
    try {
      const raw = localStorage.getItem(variant.knownColumnsKey);
      known = raw
        ? (JSON.parse(raw) as string[])
        : allIds.filter(
            (id) => !variant.columnsAddedBeforeTracking.includes(id)
          );
    } catch {
      return;
    }
    const added = variant.defaultConfig.visibleColumnIds.filter(
      (id) => !known.includes(id) && !visibleColumnIds.includes(id)
    );
    if (added.length > 0) setVisibleColumnIds([...visibleColumnIds, ...added]);
    try {
      localStorage.setItem(variant.knownColumnsKey, JSON.stringify(allIds));
    } catch {
      // Private mode / blocked storage: the columns just stay as they are.
    }
    // Once on mount: later visibility changes are the user's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Ideal margin ("Margen % (ideal)" + accept in "Inv. Plat. Nec. (Total)") ─
  // Drafts keyed by associatedCampaignId; while one exists its row shows the
  // investment that margin would leave, which is what "Aceptar" sends.
  const [marginDrafts, setMarginDrafts] = useState<Record<string, string>>({});
  // Líneas de DV360 con el desplegable de line items abierto.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [acceptRequest, setAcceptRequest] = useState<AcceptIdealRequest | null>(
    null
  );
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptIdealMargin, { isLoading: isAccepting }] =
    useAcceptAssociatedCampaignIdealMarginMutation();

  const setDraft = (rowId: string, value: string | undefined) =>
    setMarginDrafts((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[rowId];
      else next[rowId] = value;
      return next;
    });

  const confirmAccept = async (
    description: string | null,
    lineItemBudgets: LineItemBudgetTarget[],
    manualLineItems: LineItemBudgetChange[]
  ) => {
    if (!acceptRequest) return;
    const { row, margin } = acceptRequest;
    try {
      const res = await acceptIdealMargin({
        params: {
          briefingId: row.briefingId,
          associatedId: row.associatedCampaignId,
        },
        body: {
          margin,
          description,
          ...(lineItemBudgets.length > 0 ? { lineItemBudgets } : {}),
        },
      }).unwrap();
      const updated = res.data?.lineItemsUpdated.length ?? 0;
      const lineItemsNote =
        updated > 0
          ? ` y ${updated} line item${updated === 1 ? "" : "s"} con presupuesto fijo`
          : "";
      // Line items de YouTube: hay que ponerles el presupuesto a mano en DV360
      const manualNote =
        res.data?.dv360Status === "APPLIED" && manualLineItems.length > 0
          ? `. Pon a mano en DV360 (Budget and pacing): ${manualLineItems
              .map((c) => `${c.name} en ${fmtMoneyDetailed(c.budgetAfter, 2)}`)
              .join(", ")}`
          : "";
      setDraft(row.associatedCampaignId, undefined);
      setAcceptRequest(null);
      dispatch(
        setToastMessage({
          message:
            res.data?.dv360Status === "APPLIED"
              ? `Margen guardado ${marginFromText(row)} y presupuesto actualizado en DV360${lineItemsNote}${manualNote}`
              : `Margen guardado ${marginFromText(row)}. Cambia la inversión a mano en ${row.platform}`,
          severity: manualNote ? "warning" : "success",
          ...(manualNote ? { autoHideDuration: 30000 } : {}),
        })
      );
    } catch (err) {
      setAcceptError(
        getErrorMessage(err) || "No se ha podido aceptar el margen ideal"
      );
    }
  };

  const hasFieldFilters = Object.values(fieldFilters).some((v) => v.length > 0);
  const hasFilters =
    statusFilter.length > 0 || textFilter.trim() !== "" || hasFieldFilters;

  // ── Sticky synced horizontal scrollbar refs ──────────────────────────────
  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const containerScrollRef = useRef<HTMLDivElement | null>(null);
  const syncingRef = useRef(false);
  const [innerWidth, setInnerWidth] = useState(0);

  // ── Cell selection (click = select one, Ctrl/Cmd+click = add/remove) ─────
  // Keyed by "<rowId>::<colId>" so the highlight survives scrolling and
  // re-renders — it's just React state, not tied to what's in the viewport.
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  // The most recently clicked cell — its whole row and column get a subtle
  // crosshair highlight in addition to the cell's own outline.
  const [lastSelectedCell, setLastSelectedCell] = useState<{
    rowId: string;
    colId: string;
  } | null>(null);
  const cellKey = (rowId: string, colId: string) => `${rowId}::${colId}`;
  const handleCellClick = (
    e: React.MouseEvent,
    rowId: string,
    colId: string
  ) => {
    const key = cellKey(rowId, colId);
    setSelectedCells((prev) => {
      if (e.ctrlKey || e.metaKey) {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
          setLastSelectedCell((last) =>
            last && last.rowId === rowId && last.colId === colId ? null : last
          );
        } else {
          next.add(key);
          setLastSelectedCell({ rowId, colId });
        }
        return next;
      }
      if (prev.size === 1 && prev.has(key)) {
        setLastSelectedCell(null);
        return new Set();
      }
      setLastSelectedCell({ rowId, colId });
      return new Set([key]);
    });
  };

  // ── Column resize ─────────────────────────────────────────────────────────
  // Draft width of the column currently being dragged (live feedback only;
  // committed to persisted config on mouseup).
  const [resizeDraft, setResizeDraft] = useState<{
    id: string;
    width: number;
  } | null>(null);
  const resizeRef = useRef<{
    id: string;
    startX: number;
    startWidth: number;
    minWidth: number;
    currentWidth: number;
  } | null>(null);
  const rafRef = useRef<number | null>(null);

  const widthOf = useCallback(
    (col: ColumnDef): number => {
      if (resizeDraft && resizeDraft.id === col.id) return resizeDraft.width;
      return columnWidths[col.id] ?? defaultColumnWidth(col);
    },
    [resizeDraft, columnWidths]
  );

  const onResizeMove = useCallback((e: MouseEvent) => {
    const r = resizeRef.current;
    if (!r) return;
    r.currentWidth = Math.max(
      r.minWidth,
      r.startWidth + (e.clientX - r.startX)
    );
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const rr = resizeRef.current;
        if (rr) setResizeDraft({ id: rr.id, width: rr.currentWidth });
      });
    }
  }, []);

  const onResizeUp = useCallback(() => {
    const r = resizeRef.current;
    document.removeEventListener("mousemove", onResizeMove);
    document.removeEventListener("mouseup", onResizeUp);
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    resizeRef.current = null;
    if (r) {
      setColumnWidth(r.id, r.currentWidth);
      setResizeDraft(null);
    }
  }, [onResizeMove, setColumnWidth]);

  const startResize = (e: React.MouseEvent, col: ColumnDef) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[col.id] ?? defaultColumnWidth(col);
    resizeRef.current = {
      id: col.id,
      startX: e.clientX,
      startWidth,
      minWidth: col.minWidth ?? 60,
      currentWidth: startWidth,
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onResizeMove);
    document.addEventListener("mouseup", onResizeUp);
  };

  // Cleanup listeners if unmounted mid-drag.
  useEffect(
    () => () => {
      document.removeEventListener("mousemove", onResizeMove);
      document.removeEventListener("mouseup", onResizeUp);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    },
    [onResizeMove, onResizeUp]
  );

  // ── Derive enriched rows + apply filters ─────────────────────────────────
  const enrichedRows: EnrichedRow[] = useMemo(
    () => rows.map(enrichRow),
    [rows]
  );
  const savedRowsById = useMemo(
    () => new Map(enrichedRows.map((r) => [r.associatedCampaignId, r])),
    [enrichedRows]
  );

  // What the table shows: saved rows with any margin draft applied, so the
  // investment to accept and the footer totals move while a margin is typed.
  const displayRows: EnrichedRow[] = useMemo(
    () =>
      enrichedRows.map((r) => {
        const draft = marginDrafts[r.associatedCampaignId];
        const margin = draft === undefined ? null : parseMarginDraft(draft);
        return margin === null ? r : applyMarginDraft(r, margin);
      }),
    [enrichedRows, marginDrafts]
  );

  /** Opens the accept dialog with the row's ideal margin (edited or not). */
  const requestAccept = (row: EnrichedRow) => {
    const saved = savedRowsById.get(row.associatedCampaignId);
    if (!saved || row.marginIdeal == null) return;
    const margin = Math.round(row.marginIdeal * 100) / 100;
    if (margin < 0 || margin > 100) return;
    if (investmentForMargin(saved, margin) < saved.totalPlatformCost - 0.01)
      return;
    setAcceptError(null);
    setAcceptRequest({
      row: saved,
      margin,
      investment: investmentForMargin(saved, margin),
    });
  };

  // Distinct selectable values per filterable field (sorted, non-empty). A
  // field `narrowedBy` another only offers values from rows matching that
  // other field's selection (briefings of the chosen clients), and a grouped
  // field sorts by group first so the dropdown headings come out in order.
  const facets = useMemo(() => {
    const map: Record<string, FacetOption[]> = {};
    FILTER_FIELDS.forEach((f) => {
      const parent = f.narrowedBy
        ? FILTER_FIELDS.find((p) => p.key === f.narrowedBy)
        : undefined;
      const parentSelected = parent ? (fieldFilters[parent.key] ?? []) : [];
      const options = new Map<string, FacetOption>();
      enrichedRows.forEach((r) => {
        if (parent && parentSelected.length > 0) {
          if (!parentSelected.includes(parent.getValue(r))) return;
        }
        const v = f.getValue(r);
        if (v && !options.has(v)) {
          options.set(v, {
            value: v,
            label: f.formatOption(v),
            group: f.groupBy?.(r),
          });
        }
      });
      map[f.key] = Array.from(options.values()).sort(
        (a, b) =>
          (a.group ?? "").localeCompare(b.group ?? "") ||
          a.label.localeCompare(b.label)
      );
    });
    return map;
  }, [enrichedRows, fieldFilters]);

  const filteredRows = useMemo(() => {
    const text = textFilter.trim().toLowerCase();
    return displayRows.filter((r) => {
      if (statusFilter.length > 0 && !statusFilter.includes(r.status))
        return false;
      for (const f of FILTER_FIELDS) {
        const selected = fieldFilters[f.key];
        if (
          selected &&
          selected.length > 0 &&
          !selected.includes(f.getValue(r))
        )
          return false;
      }
      if (text) {
        const haystack = [
          r.companyName ?? "",
          r.briefingName ?? "",
          r.associatedCampaignName ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(text)) return false;
      }
      return true;
    });
  }, [displayRows, statusFilter, textFilter, fieldFilters]);

  // ── Totals (footer row) ──────────────────────────────────────────────────
  // Computed over filteredRows, so the footer always totals what is on screen
  // and the CSV export reuses the exact same numbers.
  const totals = useMemo(() => computeTotals(filteredRows), [filteredRows]);

  // ── Visible columns + sector grouping for header ─────────────────────────
  const orderedColumns = useMemo(
    () => orderColumns(columnOrder, columns),
    [columnOrder, columns]
  );
  // Frozen columns always come first so the sticky offsets stay contiguous.
  const visibleColumns: ColumnDef[] = useMemo(() => {
    const visible = orderedColumns.filter((c) =>
      visibleColumnIds.includes(c.id)
    );
    return [
      ...visible.filter((c) => c.pinned),
      ...visible.filter((c) => !c.pinned),
    ];
  }, [orderedColumns, visibleColumnIds]);

  /** Moves a column to where another one is (before it). */
  const moveColumn = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const ids = orderedColumns.map((c) => c.id).filter((id) => id !== fromId);
    ids.splice(ids.indexOf(toId), 0, fromId);
    setColumnOrder(ids);
  };

  // Rows in the chosen order (filters already applied). Missing values last.
  const sortedRows = useMemo(
    () => sortRows(filteredRows, sort, columns),
    [filteredRows, sort, columns]
  );

  const toggleSort = (colId: string) => {
    if (sort?.columnId !== colId)
      setSort({ columnId: colId, direction: "asc" });
    else if (sort.direction === "asc")
      setSort({ columnId: colId, direction: "desc" });
    else setSort(null);
  };

  // Frozen (pinned) columns + their cumulative left offsets, computed from the
  // same widthOf used by the <colgroup> so offsets always match render widths.
  const { pinnedIds, leftOffset, lastPinnedId, firstPinnedId } = useMemo(() => {
    const offsets = new Map<string, number>();
    let acc = 0;
    let last: string | null = null;
    let first: string | null = null;
    const ids = new Set<string>();
    visibleColumns
      .filter((c) => c.pinned)
      .forEach((c) => {
        if (first === null) first = c.id;
        offsets.set(c.id, acc);
        acc += widthOf(c);
        last = c.id;
        ids.add(c.id);
      });
    return {
      pinnedIds: ids,
      leftOffset: offsets,
      lastPinnedId: last,
      firstPinnedId: first,
    };
  }, [visibleColumns, widthOf]);

  /** Sector header cells: one per run of consecutive columns of the same
   *  sector (frozen ones apart), so they line up however columns are moved. */
  const sectorRuns = useMemo(() => {
    const runs: { sector: SectorId; pinned: boolean; span: number }[] = [];
    visibleColumns.forEach((c) => {
      const pinned = !!c.pinned;
      const last = runs[runs.length - 1];
      if (last && last.sector === c.sector && last.pinned === pinned)
        last.span++;
      else runs.push({ sector: c.sector, pinned, span: 1 });
    });
    return runs;
  }, [visibleColumns]);

  // ── Measure table width for the synced top scrollbar ──────────────────────
  // Re-measure on rows/visibility/width changes (resizing <col> doesn't resize
  // the container, so ResizeObserver alone won't catch width edits).
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const node = containerScrollRef.current;
    if (!node) return;
    const measure = () => {
      const table = node.querySelector("table");
      if (table) setInnerWidth(table.scrollWidth);
      setContainerWidth(node.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [rows, visibleColumnIds, columnWidths, resizeDraft]);

  // The fake top scrollbar only makes sense when the table is actually wider
  // than its container — otherwise it renders as an empty bar with nothing
  // to scroll.
  const needsHorizontalScroll = innerWidth > containerWidth;
  // La flecha del desplegable de line items va en la primera columna visible.
  const toggleColId = firstPinnedId ?? visibleColumns[0]?.id ?? null;

  const syncFromTop = () => {
    if (
      syncingRef.current ||
      !containerScrollRef.current ||
      !topScrollRef.current
    )
      return;
    syncingRef.current = true;
    containerScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  };
  const syncFromContainer = () => {
    if (
      syncingRef.current ||
      !containerScrollRef.current ||
      !topScrollRef.current
    )
      return;
    syncingRef.current = true;
    topScrollRef.current.scrollLeft = containerScrollRef.current.scrollLeft;
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  };

  // The "TOTAL · n" label goes in the first visible column that has no total of
  // its own, so hiding the identification columns never hides the label.
  const labelColumnId = visibleColumns.find((c) => !getColumnTotal(c.id))?.id;

  // ── CSV export: the columns on screen, in their order, rows as filtered and
  // sorted. «;» and decimal comma so Spanish Excel opens it in columns with
  // numbers it can add up.
  const exportToCsv = () => {
    const BOM = "﻿";

    const headers = visibleColumns.map((c) => c.label);
    const csvRows = sortedRows.map((row) =>
      visibleColumns.map((c) => spreadsheetCell(c.csvValue(row))).join(";")
    );

    // Same TOTAL row the footer shows, so an exported sheet doesn't have to
    // re-derive it (and cannot disagree with the screen).
    const totalsRow = visibleColumns
      .map((c) => {
        const total = getColumnTotal(c.id);
        const value = total ? (totals.get(c.id) ?? null) : null;
        if (total && value != null)
          return spreadsheetCell(totalCsvValue(total, value));
        return c.id === labelColumnId ? "TOTAL" : "";
      })
      .join(";");

    const csv = [
      headers.map(spreadsheetCell).join(";"),
      ...csvRows,
      ...(filteredRows.length > 0 ? [totalsRow] : []),
    ].join("\r\n");
    const blob = new Blob([BOM, csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const suffix = hasFilters ? "_filtered" : "";
    link.href = url;
    link.download = `WIP_${variant.csvName}${suffix}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Opaque footer surface — the pinned total cells must cover scrolled content.
  const totalsBg = theme.palette.grey[100];

  const handleClearFilters = () => {
    setStatusFilter([]);
    setTextFilter("");
    FILTER_FIELDS.forEach((f) => setFieldFilter(f.key, []));
  };

  // Header label: the column name, an "API" marker when the value comes straight
  // from the platform API, and a tooltip explaining what the column means.
  // Plain JSX factory (not a component) to keep column-prop access out of
  // react/prop-types, same as renderResizeHandle below.
  const renderHeaderLabel = (col: ColumnDef) => {
    const meta = getColumnMeta(col.id);
    return (
      <Tooltip
        title={
          <ColumnTooltip
            label={col.label}
            source={meta.source}
            description={meta.description}
            inputs={meta.inputs}
          />
        }
        placement="top"
        enterDelay={350}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            maxWidth: "100%",
            verticalAlign: "middle",
          }}
        >
          {meta.source === "api" && <SourceBadge source={meta.source} />}
          <Box
            component="span"
            sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {col.label}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  // A footer total cell: the aggregated value plus a tooltip spelling out how
  // it was rolled up (sum, weighted ratio, or why it shows "—").
  const renderTotalValue = (col: ColumnDef) => {
    const total = getColumnTotal(col.id);
    if (!total) return null;
    const value = totals.get(col.id) ?? null;
    return (
      <Tooltip
        title={
          <Box sx={{ py: 0.5 }}>
            <Box sx={{ fontWeight: 700, fontSize: 12, mb: 0.25 }}>
              Total · {col.label}
            </Box>
            <Box sx={{ fontSize: 11.5, lineHeight: 1.45 }}>{total.hint}</Box>
          </Box>
        }
        placement="top"
        enterDelay={350}
      >
        <Box component="span">
          {value == null ? "—" : formatTotal(total, value)}
        </Box>
      </Tooltip>
    );
  };

  // A resize handle on the right edge of a header cell (plain JSX factory —
  // not a component, to keep column-prop access out of react/prop-types).
  const renderResizeHandle = (col: ColumnDef) => (
    <Box
      onMouseDown={(e) => startResize(e, col)}
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        width: 6,
        height: "100%",
        cursor: "col-resize",
        userSelect: "none",
        zIndex: 1,
        "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
        ...(resizeDraft?.id === col.id
          ? { backgroundColor: alpha(theme.palette.primary.main, 0.6) }
          : {}),
      }}
    />
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      {/* Toolbar — title + filters + column menu + CSV */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontFamily: "Montserrat", fontWeight: 700 }}
          >
            {title}
          </Typography>
          <Typography variant="body2" color={theme.palette.grey[600]}>
            {t("wip.subcampaignCount", "{{count}} subcampaña(s)", {
              count: filteredRows.length,
            })}
            {hasFilters
              ? ` ${t("wip.filteredFrom", "(filtrado de {{count}})", {
                  count: rows.length,
                })}`
              : ""}
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          <Filters
            statusFilter={statusFilter}
            textFilter={textFilter}
            fieldFilters={fieldFilters}
            facets={facets}
            onStatusChange={setStatusFilter}
            onTextChange={setTextFilter}
            onFieldFilterChange={setFieldFilter}
            onClear={handleClearFilters}
          />
          <ViewsMenu
            tableKey={variant.tableKey}
            config={config}
            defaults={variant.defaultConfig}
            presets={variant.presets}
            activeViewId={activeViewId}
            onApply={applyView}
          />
          <ColumnVisibilityMenu
            visibleIds={visibleColumnIds}
            onChange={setVisibleColumnIds}
            columns={columns}
          />
          <ColumnInfoDialog columns={columns} />
          <Tooltip title={t("wip.exportCsv", "Exportar CSV")}>
            <span>
              <IconButton
                color="primary"
                onClick={exportToCsv}
                disabled={filteredRows.length === 0}
                sx={{ border: `1px solid ${theme.palette.primary.light}` }}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* Sticky synced horizontal scrollbar — only shown when the table is
          actually wider than the container, otherwise it's an empty bar
          with nothing to scroll. */}
      {needsHorizontalScroll && (
        <Box
          ref={topScrollRef}
          onScroll={syncFromTop}
          sx={{
            overflowX: "scroll",
            overflowY: "hidden",
            position: "sticky",
            top: 0,
            zIndex: 5,
            backgroundColor: theme.palette.background.paper,
            height: 14,
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
            // Force a visibly draggable thumb — without this, OS overlay
            // scrollbars stay hidden until hovered and the bar looks like
            // empty dead space instead of a scrollbar.
            scrollbarWidth: "thin",
            scrollbarColor: `${theme.palette.grey[400]} transparent`,
            "&::-webkit-scrollbar": {
              height: 10,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: 10,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
          }}
        >
          <Box sx={{ width: innerWidth, height: 1 }} />
        </Box>
      )}

      <TableContainer
        ref={containerScrollRef}
        onScroll={syncFromContainer}
        sx={{ overflowX: "auto", maxHeight: "70vh" }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
          <colgroup>
            {visibleColumns.map((col) => (
              <col key={col.id} style={{ width: widthOf(col) }} />
            ))}
          </colgroup>
          <TableHead>
            {/* Row 1 — sector headers (identification split into pinned/rest) */}
            <TableRow>
              {sectorRuns.map(({ sector, pinned, span }, index) => (
                <TableCell
                  key={`${sector}-${index}`}
                  colSpan={span}
                  align="center"
                  sx={{
                    ...headSx,
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.grey[800],
                    borderBottom: `1px solid ${theme.palette.grey[300]}`,
                    fontSize: 11,
                    textTransform: "uppercase" as const,
                    letterSpacing: 0.5,
                    ...(pinned
                      ? {
                          position: "sticky",
                          left: 0,
                          zIndex: 4,
                          borderRight: `2px solid ${theme.palette.grey[300]}`,
                        }
                      : {}),
                  }}
                >
                  {SECTOR_LABEL[sector]}
                </TableCell>
              ))}
            </TableRow>
            {/* Row 2 — column labels */}
            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
              {visibleColumns.map((col) => {
                const isPinned = pinnedIds.has(col.id);
                const isLastCol = lastSelectedCell?.colId === col.id;
                const isDropTarget =
                  dropColId === col.id &&
                  dragColId !== null &&
                  dragColId !== col.id;
                return (
                  <TableCell
                    key={col.id}
                    align={col.align ?? "left"}
                    onDragOver={(e) => {
                      if (!dragColId || isPinned) return;
                      e.preventDefault();
                      if (dropColId !== col.id) setDropColId(col.id);
                    }}
                    onDragLeave={() =>
                      setDropColId((cur) => (cur === col.id ? null : cur))
                    }
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragColId && !isPinned) moveColumn(dragColId, col.id);
                      setDragColId(null);
                      setDropColId(null);
                    }}
                    sx={{
                      ...headSx,
                      position: "sticky",
                      top: SECTOR_ROW_HEIGHT,
                      backgroundColor: theme.palette.grey[50],
                      ...(isDropTarget
                        ? {
                            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                          }
                        : {}),
                      ...(dragColId === col.id ? { opacity: 0.5 } : {}),
                      ...(isPinned
                        ? {
                            left: leftOffset.get(col.id),
                            zIndex: 4,
                            ...(col.id === lastPinnedId
                              ? {
                                  borderRight: `2px solid ${theme.palette.grey[300]}`,
                                }
                              : {}),
                          }
                        : {}),
                      ...(isLastCol
                        ? {
                            boxShadow: `inset 0 0 0 1000px ${alpha(
                              theme.palette.primary.main,
                              0.2
                            )}`,
                          }
                        : {}),
                    }}
                  >
                    <Box
                      component="span"
                      draggable={!isPinned}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", col.id);
                        setDragColId(col.id);
                      }}
                      onDragEnd={() => {
                        setDragColId(null);
                        setDropColId(null);
                      }}
                      sx={{
                        display: "inline-flex",
                        maxWidth: "100%",
                        cursor: isPinned ? "default" : "grab",
                      }}
                    >
                      <TableSortLabel
                        active={sort?.columnId === col.id}
                        direction={
                          sort?.columnId === col.id ? sort.direction : "asc"
                        }
                        onClick={() => toggleSort(col.id)}
                        sx={{
                          maxWidth: "100%",
                          "& .MuiTableSortLabel-icon": {
                            fontSize: 14,
                            mx: 0.25,
                          },
                        }}
                      >
                        {renderHeaderLabel(col)}
                      </TableSortLabel>
                    </Box>
                    {renderResizeHandle(col)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={Math.max(1, visibleColumns.length)}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography color={theme.palette.grey[600]}>
                    {rows.length === 0
                      ? "Sin subcampañas activas para este mes."
                      : "Ninguna subcampaña coincide con los filtros."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row) => {
                const statusColor = STATUS_COLOR[row.status];
                // A direct buy (no API, metrics typed in by hand) swaps the
                // status tint for its own colour so it reads apart.
                const rowColor = row.isDirectBuy
                  ? DIRECT_BUY_ACCENT
                  : statusColor;
                const stickyBg = flattenOnSurface(
                  rowColor,
                  ROW_TINT_ALPHA,
                  theme.palette.background.paper
                );
                const isExpanded = expandedIds.has(row.associatedCampaignId);
                return (
                  <React.Fragment key={row.associatedCampaignId}>
                    <TableRow
                      hover
                      sx={{
                        backgroundColor: row.isDirectBuy
                          ? DIRECT_BUY_BG[theme.palette.mode]
                          : alpha(statusColor, ROW_TINT_ALPHA),
                      }}
                    >
                      {visibleColumns.map((col) => {
                        const isPinned = pinnedIds.has(col.id);
                        const tone = col.tone?.(row);
                        const isSelected = selectedCells.has(
                          cellKey(row.associatedCampaignId, col.id)
                        );
                        const isLastRow =
                          lastSelectedCell?.rowId === row.associatedCampaignId;
                        const isLastCol = lastSelectedCell?.colId === col.id;
                        const savedRow = savedRowsById.get(
                          row.associatedCampaignId
                        );
                        const canChangeMargin =
                          canEditMargin &&
                          isMarginEditable(row) &&
                          savedRow?.marginIdeal != null;
                        const isManualEditable =
                          col.editable === "manual" &&
                          !!col.manualField &&
                          row.isDirectBuy &&
                          canEditDirectBuys;
                        const isEditable =
                          (col.editable === "margin" && canChangeMargin) ||
                          isManualEditable;
                        const acceptable =
                          col.action === "acceptIdeal" &&
                          canChangeMargin &&
                          row.marginIdeal != null;
                        // Why "Aceptar" is disabled, if it is.
                        const acceptBlocked = !acceptable
                          ? null
                          : row.marginIdeal! < 0 || row.marginIdeal! > 100
                            ? "El margen tiene que estar entre 0 y 100: edita el margen ideal"
                            : (row.idealInvestment ?? 0) <
                                row.totalPlatformCost - 0.01
                              ? `Con ese margen la inversión quedaría por debajo de lo ya gastado (${fmtMoney(row.totalPlatformCost, 2)})`
                              : null;
                        return (
                          <TableCell
                            key={col.id}
                            align={col.align ?? "left"}
                            onClick={(e) =>
                              handleCellClick(
                                e,
                                row.associatedCampaignId,
                                col.id
                              )
                            }
                            sx={{
                              ...cellSx,
                              cursor: "pointer",
                              ...(col.ideal
                                ? {
                                    backgroundColor:
                                      IDEAL_BG[theme.palette.mode],
                                  }
                                : {}),
                              ...(isEditable
                                ? {
                                    outline: `1px dashed ${
                                      EDITABLE_BORDER[theme.palette.mode]
                                    }`,
                                    outlineOffset: "-2px",
                                  }
                                : {}),
                              ...(col.emphasis
                                ? {
                                    backgroundColor:
                                      EMPHASIS_BG[theme.palette.mode],
                                  }
                                : {}),
                              ...(tone
                                ? {
                                    color: toneStyle[tone].color,
                                    backgroundColor: toneStyle[tone].bg,
                                    fontWeight: 600,
                                  }
                                : {}),
                              ...(isPinned
                                ? {
                                    position: "sticky",
                                    left: leftOffset.get(col.id),
                                    zIndex: 2,
                                    backgroundColor: stickyBg,
                                    ...(col.id === firstPinnedId
                                      ? {
                                          borderLeft: `3px solid ${rowColor}`,
                                        }
                                      : {}),
                                    ...(col.id === lastPinnedId
                                      ? {
                                          borderRight: `2px solid ${theme.palette.grey[300]}`,
                                        }
                                      : {}),
                                  }
                                : {}),
                              ...(isLastRow || isLastCol
                                ? {
                                    boxShadow: `inset 0 0 0 1000px ${alpha(
                                      theme.palette.primary.main,
                                      0.16
                                    )}`,
                                  }
                                : {}),
                              ...(isSelected
                                ? {
                                    outline: `2px solid ${theme.palette.primary.main}`,
                                    outlineOffset: "-2px",
                                    zIndex: isPinned ? 3 : 1,
                                  }
                                : {}),
                            }}
                          >
                            {row.hasLineItemBreakdown &&
                              col.id === toggleColId && (
                                <Tooltip
                                  title={
                                    isExpanded
                                      ? "Ocultar line items"
                                      : "Ver line items de la IO y su presupuesto en DV360"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    aria-label="Line items"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleExpanded(row.associatedCampaignId);
                                    }}
                                    sx={{ p: 0, mr: 0.5 }}
                                  >
                                    {isExpanded ? (
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    ) : (
                                      <KeyboardArrowRightIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            {isManualEditable ? (
                              <DirectBuyMetricCell
                                row={row}
                                field={col.manualField!}
                              />
                            ) : isEditable && savedRow?.marginIdeal != null ? (
                              <MarginEditCell
                                value={savedRow.marginIdeal}
                                draft={marginDrafts[row.associatedCampaignId]}
                                onStart={() =>
                                  setDraft(
                                    row.associatedCampaignId,
                                    (
                                      Math.round(savedRow.marginIdeal! * 100) /
                                      100
                                    ).toString()
                                  )
                                }
                                onDraftChange={(v) =>
                                  setDraft(row.associatedCampaignId, v)
                                }
                                onCancel={() =>
                                  setDraft(row.associatedCampaignId, undefined)
                                }
                                onSave={() => requestAccept(row)}
                              />
                            ) : acceptable ? (
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                }}
                              >
                                {col.render(row)}
                                <Tooltip
                                  title={
                                    acceptBlocked ??
                                    (row.dv360AutoApply
                                      ? `Guarda el margen ideal ${marginFromText(row)} y envía esta inversión al presupuesto de la IO en DV360`
                                      : `Guarda el margen ideal ${marginFromText(row)}. La inversión hay que cambiarla a mano en ${row.platform}`)
                                  }
                                >
                                  <span>
                                    <Button
                                      disabled={acceptBlocked !== null}
                                      size="small"
                                      variant="contained"
                                      color={
                                        row.dv360AutoApply
                                          ? "primary"
                                          : "warning"
                                      }
                                      startIcon={
                                        row.dv360AutoApply ? (
                                          <CloudUploadIcon />
                                        ) : (
                                          <CheckIcon />
                                        )
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        requestAccept(row);
                                      }}
                                      sx={{
                                        minWidth: 0,
                                        px: 0.75,
                                        py: 0,
                                        fontSize: 11,
                                        lineHeight: 1.6,
                                        "& .MuiButton-startIcon": { mr: 0.25 },
                                        "& .MuiButton-startIcon svg": {
                                          fontSize: 14,
                                        },
                                      }}
                                    >
                                      Aceptar
                                    </Button>
                                  </span>
                                </Tooltip>
                              </Box>
                            ) : (
                              col.render(row)
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell
                          colSpan={Math.max(1, visibleColumns.length)}
                          sx={{
                            p: 0,
                            backgroundColor: theme.palette.background.default,
                            borderLeft: `3px solid ${statusColor}`,
                          }}
                        >
                          <LineItemsPanel
                            associatedCampaignId={row.associatedCampaignId}
                            width={containerWidth}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
          {/* Totals — sticky at the bottom, aggregating the filtered rows.
              Columns whose roll-up would be meaningless (días, % tiempo,
              volúmenes de objetivos mezclados…) stay empty on purpose. */}
          {filteredRows.length > 0 && (
            <TableFooter>
              <TableRow>
                {visibleColumns.map((col) => {
                  const total = getColumnTotal(col.id);
                  const value = total ? (totals.get(col.id) ?? null) : null;
                  const tone =
                    total?.tone && value != null
                      ? total.tone(value)
                      : undefined;
                  const isPinned = pinnedIds.has(col.id);
                  return (
                    <TableCell
                      key={col.id}
                      align={col.align ?? "left"}
                      sx={{
                        ...cellSx,
                        position: "sticky",
                        bottom: 0,
                        zIndex: isPinned ? 4 : 3,
                        fontWeight: 700,
                        fontSize: 12,
                        color: theme.palette.text.primary,
                        backgroundColor: totalsBg,
                        borderTop: `2px solid ${theme.palette.grey[400]}`,
                        ...(tone ? { color: toneStyle[tone].color } : {}),
                        ...(isPinned
                          ? {
                              left: leftOffset.get(col.id),
                              ...(col.id === lastPinnedId
                                ? {
                                    borderRight: `2px solid ${theme.palette.grey[300]}`,
                                  }
                                : {}),
                            }
                          : {}),
                      }}
                    >
                      {col.id === labelColumnId
                        ? `TOTAL · ${filteredRows.length}`
                        : renderTotalValue(col)}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      {variant.showLegend && <WipLegend />}

      <AcceptIdealDialog
        request={acceptRequest}
        isSaving={isAccepting}
        error={acceptError}
        onClose={() => setAcceptRequest(null)}
        onConfirm={confirmAccept}
      />
    </Paper>
  );
};

export default BuyMediaWipTable;
